/**
 * KAN-834: вход в приложение менеджера по одноразовой ссылке /m/login/:uid/:token.
 * KAN-867: открытие страницы ссылку НЕ обменивает. Превью ссылок в мессенджерах,
 * сканеры ссылок и встроенные браузеры открывали страницу раньше человека и гасили
 * одноразовую ссылку. Обмен уходит только по submit() (кнопка "Войти"), ровно
 * один раз: ref защищает от двойного нажатия в одном такте.
 * KAN-925: вошедшие аккаунты браузера не выходят ни при открытии, ни при обмене:
 * вход добавляет аккаунт менеджера и делает его активным во вкладке.
 * Успех: кеш прошлого пользователя очищается, аккаунт сохраняется (startAuthSession),
 * адрес ссылки заменяется на /m. Ошибка: текст от backend (использована, истекла)
 * или общий.
 */
import { useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { appLinkApi } from '@/services/api/appLink';
import { startAuthSession } from '@/lib/authSession';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { MOBILE_HOME } from '@/lib/mobileApp';

export const APP_LINK_ERROR = 'Ссылка недействительна, попросите новую';

export type AppLinkLoginState = 'idle' | 'pending' | 'success' | 'error';

export function useAppLinkLogin(uid: string, token: string) {
  const queryClient = useQueryClient();
  const started = useRef(false);
  const exchange = useMutation({
    // KAN-834: обмен идёт без Authorization (apiClient не ставит токен на
    // запросы входа), поэтому протухший токен вошедшего аккаунта не мешает.
    mutationFn: () => appLinkApi.exchange(uid, token),
    onSuccess: (data) => {
      queryClient.clear();
      startAuthSession(data.access, data.refresh, data.user);
      // Использованная ссылка уходит из адресной строки и истории: "На экран Домой"
      // на iPhone берёт текущий адрес, а иконка с погашенной ссылкой не войдёт.
      // Дальше страница входа сама переходит на /m/start (KAN-845).
      window.history.replaceState(window.history.state, '', MOBILE_HOME);
    },
  });
  const { mutate } = exchange;

  const submit = useCallback(() => {
    if (started.current) return;
    started.current = true;
    mutate();
  }, [mutate]);

  const state: AppLinkLoginState = exchange.isSuccess
    ? 'success'
    : exchange.isError
      ? 'error'
      : exchange.isPending
        ? 'pending'
        : 'idle';
  return {
    state,
    error: exchange.isError ? extractErrorMessage(exchange.error, APP_LINK_ERROR) : null,
    submit,
  };
}
